import apiInstance from '@api';
import { JiraIssueFields } from '@api/support/SupportAPI';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import FormFieldsContainer from '../../../Form/components/FormFieldContainer';
import SchemaRenderer from '../../../Form/components/SchemaRenderer';
import toastMessage from '../../../Toast';

import {
  FEEDBACK_DEFAULT_VALUES,
  FEEDBACK_REQUEST_TYPE,
} from '../../constants';
import { FeedbackFormValues, SupportFormProps } from '../../types';
import { buildFeedbackFormSchema } from '../../utils';

import FormActions from '../FormActions';

// Feedback is emailed to the support mailbox instead of raising a task.
const FeedbackForm: FC<SupportFormProps> = ({ onCancel, onSubmitted }) => {
  const [submitting, setSubmitting] = useState(false);

  const schema = useMemo(() => buildFeedbackFormSchema(), []);
  const methods = useForm<FeedbackFormValues>({
    mode: 'onSubmit',
    defaultValues: FEEDBACK_DEFAULT_VALUES,
  });

  const handleSubmit = useCallback(
    async (values: FeedbackFormValues) => {
      setSubmitting(true);

      const payload: JiraIssueFields = {
        request_type: FEEDBACK_REQUEST_TYPE,
        title: 'Feedback',
        description: values.feedback.trim(),
        page_url: window.location.href,
      };

      try {
        await apiInstance.support.createJiraIssue(payload);

        toastMessage.success('Thank you for your feedback!');
        onSubmitted();
      } catch (error) {
        console.error('Failed to submit the feedback:', error);
        toastMessage.error(
          'Failed to submit your feedback. Please try again later.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [onSubmitted],
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <FormFieldsContainer columns={1}>
          <SchemaRenderer schema={schema} />
        </FormFieldsContainer>
        <FormActions
          submitLabel="Send Feedback"
          submitting={submitting}
          onCancel={onCancel}
        />
      </form>
    </FormProvider>
  );
};

export default FeedbackForm;
